import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, UserCheck, UserPlus, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ComunicacaoPje } from '@/services/comunicaPjeService';
import { detectJudicialSystem, normalizePersonName } from '@/utils/judicialSystem';

const LOGO = '/imgs/logos allana/letra_A_logo.png';

export interface IntimacaoClient {
  id: string;
  name: string;
  email: string | null;
}

/** Dados enviados para o formulário de processo (página Processos) */
export interface IntimacaoPrefill {
  numeroMascara: string;
  tribunal: string;
  sistema: string;
  orgao: string;
  classe: string;
  autores: string[];
  reus: string[];
  texto: string;
  dataDisponibilizacao: string;
  link: string | null;
  client: { id: string; name: string; email: string } | null;
}

const capitalize = (value: string) => {
  const lower = value.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

interface Props {
  item: ComunicacaoPje | null;
  clients: IntimacaoClient[];
  onOpenChange: (open: boolean) => void;
}

export const RegisterFromIntimacaoDialog = ({ item, clients, onOpenChange }: Props) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);

  const autores = useMemo(() => (item?.destinatarios || []).filter((d) => d.polo !== 'P').map((d) => d.nome), [item]);
  const reus = useMemo(() => (item?.destinatarios || []).filter((d) => d.polo === 'P').map((d) => d.nome), [item]);

  // Partes da intimação que já são clientes
  const matched = useMemo(() => {
    const byName = new Map(clients.map((c) => [normalizePersonName(c.name), c]));
    const found = (item?.destinatarios || []).map((d) => byName.get(normalizePersonName(d.nome))).filter(Boolean) as IntimacaoClient[];
    return [...new Map(found.map((c) => [c.id, c])).values()];
  }, [item, clients]);

  useEffect(() => {
    if (!item) return;
    if (matched[0]) setChoice(`existing:${matched[0].id}`);
    else if (autores[0]) setChoice(`new:${autores[0]}`);
    else setChoice('');
  }, [item, matched, autores]);

  if (!item) return null;
  const system = detectJudicialSystem(item.link, item.siglaTribunal);
  const classe = item.nomeClasse ? capitalize(item.nomeClasse) : 'Processo Judicial';
  const newCandidates = (item.destinatarios || [])
    .map((d) => d.nome)
    .filter((name, index, arr) => arr.indexOf(name) === index)
    .filter((name) => !matched.some((c) => normalizePersonName(c.name) === normalizePersonName(name)));
  const otherClients = clients.filter((c) => !matched.some((m) => m.id === c.id));

  const handleContinue = async () => {
    setSaving(true);
    try {
      let client: IntimacaoPrefill['client'] = null;
      if (choice.startsWith('existing:')) {
        const found = clients.find((c) => c.id === choice.slice(9));
        if (found) client = { id: found.id, name: found.name, email: found.email || '' };
      } else if (choice.startsWith('new:')) {
        const name = choice.slice(4);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Sessão expirada. Entre novamente.');
        const { data, error } = await supabase
          .from('clients')
          .insert({ name, email: '', user_id: user.id, client_type: 'person' } as any)
          .select('id, name, email')
          .single();
        if (error) throw error;
        client = { id: data.id, name: data.name, email: data.email || '' };
        toast({ title: 'Cliente cadastrado', description: `${name} foi adicionado aos clientes. Você pode completar o cadastro dele na tela do processo.` });
      }

      const prefill: IntimacaoPrefill = {
        numeroMascara: item.numeroprocessocommascara,
        tribunal: item.siglaTribunal,
        sistema: system.label,
        orgao: item.nomeOrgao,
        classe,
        autores,
        reus,
        texto: item.texto,
        dataDisponibilizacao: item.data_disponibilizacao,
        link: item.link,
        client,
      };
      onOpenChange(false);
      navigate('/cases', { state: { intimacaoPrefill: prefill } });
    } catch (error: any) {
      toast({ title: 'Não foi possível continuar', description: error?.message || 'Tente novamente.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const row = (label: string, value?: string | null) => (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-800">{value || '—'}</dd>
    </div>
  );

  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
        <DialogHeader className="relative -m-6 mb-2 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          <img src={LOGO} alt="" aria-hidden="true" className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]" />
          <div className="relative flex items-center gap-4 text-left">
            <img src={LOGO} alt="Allana Sthéfany" className="h-12 w-12 shrink-0 select-none object-contain opacity-80" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Cadastrar a partir da intimação</p>
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">{item.numeroprocessocommascara}</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">Confira os dados e escolha o cliente. Depois você completa o cadastro.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-4">
          <dl className="grid grid-cols-1 gap-4 rounded-xl bg-[#faf7f2] p-4 sm:grid-cols-2">
            {row('Sistema', system.label)}
            {row('Classe', classe)}
            {row('Vara / órgão', item.nomeOrgao)}
            {row('Última intimação', item.data_disponibilizacao.split('-').reverse().join('/'))}
            {row('Polo ativo', autores.join(', '))}
            {row('Polo passivo', reus.join(', '))}
          </dl>

          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Cliente do processo</p>
            <div className="space-y-2">
              {matched.map((c) => (
                <label key={c.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${choice === `existing:${c.id}` ? 'border-[#c4ab83] bg-[#faf7f2]' : 'border-slate-200 hover:border-[#c4ab83]/60'}`}>
                  <input type="radio" name="client" className="accent-[#9a7f52]" checked={choice === `existing:${c.id}`} onChange={() => setChoice(`existing:${c.id}`)} />
                  <UserCheck className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm text-slate-800">{c.name}</span>
                  <span className="ml-auto rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">já é cliente</span>
                </label>
              ))}
              {newCandidates.map((name) => (
                <label key={name} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${choice === `new:${name}` ? 'border-[#c4ab83] bg-[#faf7f2]' : 'border-slate-200 hover:border-[#c4ab83]/60'}`}>
                  <input type="radio" name="client" className="accent-[#9a7f52]" checked={choice === `new:${name}`} onChange={() => setChoice(`new:${name}`)} />
                  <UserPlus className="h-4 w-4 text-[#9a7f52]" />
                  <span className="text-sm text-slate-800">Cadastrar <strong>{name}</strong> como cliente novo</span>
                </label>
              ))}
              <div className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${choice.startsWith('existing:') && !matched.some((m) => choice === `existing:${m.id}`) ? 'border-[#c4ab83] bg-[#faf7f2]' : 'border-slate-200'}`}>
                <Users className="h-4 w-4 shrink-0 text-slate-400" />
                <Select
                  value={choice.startsWith('existing:') && !matched.some((m) => choice === `existing:${m.id}`) ? choice : ''}
                  onValueChange={setChoice}
                >
                  <SelectTrigger className="h-8 border-0 px-0 text-sm shadow-none focus:ring-0">
                    <SelectValue placeholder="Escolher outro cliente já cadastrado..." />
                  </SelectTrigger>
                  <SelectContent>
                    {otherClients.map((c) => (
                      <SelectItem key={c.id} value={`existing:${c.id}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end sm:gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancelar</Button>
            <Button onClick={handleContinue} disabled={saving || !choice} className="rounded-xl bg-[#c4ab83] px-6 font-semibold text-slate-900 hover:bg-[#b39a72]">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Continuar para o cadastro
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
